import { SCHEMA_VERSION } from "./schema/version";
import type { Cable, ComponentNode, System } from "./types";

const SHARED_NODE_IDS = {
  houseBattery: "house-battery",
  startBattery: "start-battery",
  solarPanel: "solar-panel",
  solarController: "solar-controller",
  shoreCharger: "shore-charger",
  batteryMonitor: "battery-monitor",
  mainDisconnect: "main-disconnect",
  positiveBus: "positive-bus",
  negativeBus: "negative-bus",
  fuseBlock: "fuse-block",
  fridge: "fridge",
  lights: "lights",
} as const;

/** Stable ids let the comparison UI focus equivalent parts in either system. */
export const LIFEPO4_CONVERSION_NODE_IDS = {
  before: {
    ...SHARED_NODE_IDS,
    alternatorCharging: "combiner-relay",
  },
  after: {
    ...SHARED_NODE_IDS,
    alternatorCharging: "dc-dc-charger",
    bms: "battery-management-system",
    loadDisconnect: "bms-controlled-load-disconnect",
  },
} as const;

const BEFORE = LIFEPO4_CONVERSION_NODE_IDS.before;
const AFTER = LIFEPO4_CONVERSION_NODE_IDS.after;

function component(
  id: string,
  definitionId: string,
  x: number,
  y: number,
  extra: Partial<ComponentNode> = {},
): ComponentNode {
  return {
    id,
    kind: "component",
    definitionId,
    position: { x, y },
    ...extra,
  };
}

function cable(
  id: string,
  from: [nodeId: string, terminalId: string],
  to: [nodeId: string, terminalId: string],
  lengthM: number,
  extra: Partial<Cable> = {},
): Cable {
  return {
    id,
    fromNodeId: from[0],
    fromTerminalId: from[1],
    toNodeId: to[0],
    toTerminalId: to[1],
    lengthM,
    ...extra,
  };
}

function sharedNodes(chemistry: "agm" | "lifepo4"): ComponentNode[] {
  const isLithium = chemistry === "lifepo4";
  return [
    component(BEFORE.solarPanel, "solar-victron-175", 40, 20, {
      label: "Existing solar panel",
    }),
    component(BEFORE.solarController, "mppt-victron-7510", 330, 20, {
      label: "Solar charger",
      notes: isLithium
        ? "Set the exact battery profile and connect the MPPT's VE.Direct remote on/off cable to the BMS allow-to-charge path as specified in both manuals. The signal lead is not drawn here."
        : "Before conversion: configured for the installed AGM battery. Record the settings before changing them.",
    }),
    component(
      BEFORE.shoreCharger,
      isLithium ? "shore-victron-ip43-30" : "shore-noco-genius-10",
      330,
      215,
      {
      label: "Shore charger",
      notes: isLithium
        ? "Program the exact battery profile and wire the charger's remote on/off input to the BMS allow-to-charge path as specified in both manuals. The signal lead is not drawn here."
        : "Before conversion: use the 12 V AGM mode for this bank.",
      },
    ),
    component(BEFORE.houseBattery, isLithium ? "batt-victron-lfp-200" : "batt-victron-agm-110", 40, 430, {
      label: isLithium ? "LiFePO4 house bank" : "AGM house bank",
      notes: isLithium
        ? "Victron Smart Lithium requires an external BMS. Confirm charge/discharge current limits, low-temperature charging protection, terminal fuse rating, and cable ampacity for the installed bank."
        : "Document the AGM bank capacity, cable sizes, terminal fuse, and all charger settings before removal.",
    }),
    component(BEFORE.mainDisconnect, "dist-main-switch", isLithium ? 600 : 350, 430, {
      label: "House disconnect",
    }),
    component(BEFORE.batteryMonitor, "mon-smartshunt-500", isLithium ? 600 : 350, 575, {
      label: "Battery monitor",
      notes: isLithium
        ? "Commission again for 200 Ah LiFePO4: enter the battery-maker settings, fully synchronize, and verify all DC returns stay on the load side of the shunt."
        : "Before conversion: configured for the 110 Ah AGM house bank.",
    }),
    component(BEFORE.positiveBus, "dist-pos-bus", isLithium ? 900 : 700, 345, {
      label: "Positive distribution",
      studCount: 4,
    }),
    component(BEFORE.negativeBus, "dist-neg-bus", isLithium ? 900 : 700, 550, {
      label: "Negative distribution",
      studCount: 4,
    }),
    ...(isLithium
      ? [
          component(AFTER.loadDisconnect, "dist-victron-bp-100", 1110, 300, {
            label: "BMS-controlled load disconnect",
            notes:
              "Connect the BatteryProtect remote input to the Lynx Smart BMS allow-to-discharge path according to both manuals. The signal lead is not drawn here.",
          }),
        ]
      : []),
    component(BEFORE.fuseBlock, "dist-bs-5025", isLithium ? 1320 : 960, 390, {
      label: "Fused DC branches",
    }),
    component(BEFORE.fridge, "load-fridge-cfx3-35", isLithium ? 1640 : 1280, 320),
    component(BEFORE.lights, "load-led-strip", isLithium ? 1640 : 1280, 540),
    component(BEFORE.startBattery, "batt-start-80", 40, 735, {
      label: "Starter battery",
      notes: "The starter battery remains lead-acid in this example.",
    }),
  ];
}

function sharedCables(chemistry: "agm" | "lifepo4"): Cable[] {
  const common = [
    cable("solar-positive", [BEFORE.solarPanel, "pos"], [BEFORE.solarController, "pv-pos"], 3),
    cable("solar-negative", [BEFORE.solarPanel, "neg"], [BEFORE.solarController, "pv-neg"], 3),
    cable("solar-charge-positive", [BEFORE.solarController, "batt-pos"], [BEFORE.positiveBus, "c"], 1.5),
    cable("solar-charge-negative", [BEFORE.solarController, "batt-neg"], [BEFORE.negativeBus, "c"], 1.5),
    cable("shore-charge-positive", [BEFORE.shoreCharger, "batt-pos"], [BEFORE.positiveBus, "d"], 1.5),
    cable("shore-charge-negative", [BEFORE.shoreCharger, "batt-neg"], [BEFORE.negativeBus, "d"], 1.5),
    cable("branch-panel-negative", [BEFORE.negativeBus, "e"], [BEFORE.fuseBlock, "in-neg"], 0.8),
    cable("fridge-positive", [BEFORE.fuseBlock, "c1"], [BEFORE.fridge, "pos"], 3),
    cable("fridge-negative", [BEFORE.fridge, "neg"], [BEFORE.fuseBlock, "n1"], 3),
    cable("lights-positive", [BEFORE.fuseBlock, "c2"], [BEFORE.lights, "pos"], 4),
    cable("lights-negative", [BEFORE.lights, "neg"], [BEFORE.fuseBlock, "n2"], 4),
  ];

  if (chemistry === "agm") {
    return [
      ...common,
      cable(
        "house-positive",
        [BEFORE.houseBattery, "pos"],
        [BEFORE.mainDisconnect, "a"],
        0.15,
        { fuseTypeOverride: "MRBF" },
      ),
      cable("house-positive-switched", [BEFORE.mainDisconnect, "b"], [BEFORE.positiveBus, "a"], 0.65),
      cable("house-negative", [BEFORE.houseBattery, "neg"], [BEFORE.batteryMonitor, "batt-neg"], 0.35),
      cable("house-negative-monitored", [BEFORE.batteryMonitor, "load-neg"], [BEFORE.negativeBus, "a"], 0.65),
      cable("branch-panel-positive", [BEFORE.positiveBus, "e"], [BEFORE.fuseBlock, "in-pos"], 0.8),
    ];
  }

  return [
    ...common,
    cable(
      "house-positive",
      [AFTER.houseBattery, "pos"],
      [AFTER.bms, "batt-pos"],
      0.15,
      { fuseTypeOverride: "MRBF" },
    ),
    cable("house-positive-managed", [AFTER.bms, "bus-pos"], [AFTER.mainDisconnect, "a"], 0.35),
    cable("house-positive-switched", [AFTER.mainDisconnect, "b"], [AFTER.positiveBus, "a"], 0.35),
    cable("house-negative", [AFTER.houseBattery, "neg"], [AFTER.bms, "batt-neg"], 0.15),
    cable("house-negative-managed", [AFTER.bms, "bus-neg"], [AFTER.batteryMonitor, "batt-neg"], 0.3),
    cable("house-negative-monitored", [AFTER.batteryMonitor, "load-neg"], [AFTER.negativeBus, "a"], 0.35),
    cable("protected-load-feed", [AFTER.positiveBus, "e"], [AFTER.loadDisconnect, "in"], 0.45),
    cable("branch-panel-positive", [AFTER.loadDisconnect, "out"], [AFTER.fuseBlock, "in-pos"], 0.45),
  ];
}

function finish(system: System): System {
  return {
    ...system,
    // These are topology examples, not sized designs. Keeping derived fuse
    // values in the payload would leak them through battery pills and terminal
    // accessibility labels even when the cable-label layer is hidden.
    nodes: system.nodes.map((node) =>
      node.kind === "component" ? { ...node, batteryFuse: undefined } : node,
    ),
    cables: system.cables.map((candidate) => ({
      ...candidate,
      fuseAmpAuto: false,
      fuseAmpOverride: undefined,
      fuseTypeOverride: undefined,
    })),
  };
}

export function buildLeadAcidBeforeSystem(): System {
  const nodes = [
    ...sharedNodes("agm"),
    component(BEFORE.alternatorCharging, "dist-victron-cyrix-ct-120", 350, 735, {
      label: "Combiner relay",
      notes: "Lead-acid baseline: the voltage-sensing relay can parallel the starter and AGM house banks while charging.",
    }),
  ];
  const cables = [
    ...sharedCables("agm"),
    cable(
      "starter-positive",
      [BEFORE.startBattery, "pos"],
      [BEFORE.alternatorCharging, "a"],
      0.8,
      { fuseTypeOverride: "MRBF" },
    ),
    cable("combiner-house-positive", [BEFORE.alternatorCharging, "b"], [BEFORE.positiveBus, "b"], 1.8),
    cable("starter-negative", [BEFORE.startBattery, "neg"], [BEFORE.negativeBus, "b"], 1.8),
  ];

  return finish({
    name: "Before — AGM house system",
    nodes,
    cables,
    zoneDistances: {},
    version: SCHEMA_VERSION,
  });
}

export function buildLifepo4AfterSystem(): System {
  const nodes = [
    ...sharedNodes("lifepo4"),
    component(AFTER.alternatorCharging, "dcdc-victron-orion-xs-50", 350, 735, {
      label: "Current-limited alternator charging",
      notes: "Configure the Orion XS for the battery manufacturer's LiFePO4 limits and the vehicle alternator. Wire and configure its supported remote/DVCC allow-to-charge path to the Lynx Smart BMS according to both manuals, then verify engine-shutdown detection before commissioning. The signal lead is not drawn here.",
    }),
    component(AFTER.bms, "dist-victron-lynx-smart-bms-500", 330, 430, {
      label: "Compatible external BMS + contactor",
      notes: "Compatible with Victron Lithium Battery Smart. The main DC isolation boundary is shown. Battery communication and allow-to-charge/discharge signal paths still need to follow the exact BMS, charger and load manuals; Crimp does not draw those signal leads here.",
    }),
  ];
  const cables = [
    ...sharedCables("lifepo4"),
    cable(
      "starter-positive",
      [AFTER.startBattery, "pos"],
      [AFTER.alternatorCharging, "in-pos"],
      0.8,
      { fuseTypeOverride: "MRBF" },
    ),
    cable("starter-negative", [AFTER.startBattery, "neg"], [AFTER.alternatorCharging, "in-neg"], 0.8),
    cable("dcdc-house-positive", [AFTER.alternatorCharging, "out-pos"], [AFTER.positiveBus, "b"], 1.8),
    cable("dcdc-house-negative", [AFTER.alternatorCharging, "out-neg"], [AFTER.negativeBus, "b"], 1.8),
  ];

  return finish({
    name: "After — LiFePO4 conversion",
    nodes,
    cables,
    zoneDistances: {},
    version: SCHEMA_VERSION,
  });
}
